/**
 * Diagnostic overlay palette — the single source of colour for both overlays.
 *
 * Two encodings, two different jobs, deliberately kept apart:
 *
 *   FAILURE DENSITY  = sequential magnitude -> ONE hue, light->dark (blue).
 *   ACTIVE DTCs      = status -> the reserved status palette, never themed,
 *                      and never carrying meaning by colour alone.
 *
 * The overlays are MUTUALLY EXCLUSIVE modes. That is a correctness decision,
 * not a UI preference: a sequential warm ramp on screen at the same time as the
 * warm status colours would let a magnitude step impersonate a reserved status.
 * Only one encoding is ever active, so that collision cannot occur.
 *
 * VALIDATED (scripts/validate_palette.js, dark mode, surface #0a0e27 — the
 * app's chart surface at the time; the Varahi console surface is now #0B1418):
 *
 *   density ramp, --ordinal:  ALL CHECKS PASS
 *     lightness monotone / adjacent dL >= 0.06 / single hue, spread 3 degrees
 *     (Varahi brand pass: darkest step #184f95 -> #2263b4, TRACE rank 4
 *     #6d44a8 -> #7a4cb5 and COVERAGE step 1 #1f5a55 -> #256e67, the minimum
 *     same-hue lift to clear 3:1 on #0B1418; order is unchanged but the
 *     adjacent-step dL was not re-run against scripts/validate_palette.js)
 *
 *   status (the three roles the alert data actually uses):
 *     CVD separation PASS (worst adjacent 11.3 protan)
 *     normal-vision  PASS (worst adjacent 27.6)
 *     contrast       PASS (all >= 3:1)
 *     lightness band FAIL on #fab219 (L 0.811) -- ACCEPTED, not fixed. The
 *       status palette is documented "fixed - never themed", so re-stepping it
 *       would be the larger error. The prescribed mitigation is the icon+label
 *       pairing, which every consumer of STATUS below is required to render.
 *
 *   The unused "serious" role (#ec835a) is deliberately omitted: paired with
 *   warning it fails the normal-vision floor (dE 13.6). The app's alert data
 *   only has info / warning / critical, so dropping it removes the bad pair
 *   outright rather than shipping it with a caveat.
 */

/* --------------------------------------------------------------- sequential */

/** Blue ramp, low->high magnitude. Brighter = more failures on a dark surface. */
export const DENSITY_RAMP = ['#2263b4', '#256abf', '#3987e5', '#6da7ec', '#9ec5f4'];

/**
 * Two greys that must never be confused with each other or with the ramp.
 *
 * ZERO    - the part IS linked to the knowledge graph and has no recorded
 *           failures. A real, trustworthy zero.
 * UNKNOWN - the part matched no knowledge-graph entity at all, so we know
 *           nothing about it. 9 of 67 parts are in this state.
 *
 * Rendering these identically would be a lie: it would show absence of data as
 * absence of failures. UNKNOWN is dimmer and is labelled separately in the
 * legend.
 */
export const DENSITY_ZERO = '#3d4657';
export const DENSITY_UNKNOWN = '#232a38';

/**
 * Bin thresholds. Deliberately NOT linear: the real distribution over the 66
 * BOM nodes is 28 zeros, a cluster at 1-4, then a gap to 14-53. Linear bins
 * would put every non-trivial part in one bucket and waste four of five steps.
 * Each bin is [min, max] inclusive on the failure count.
 */
export const DENSITY_BINS = [
  { min: 1, max: 4, label: '1–4' },
  { min: 5, max: 19, label: '5–19' },
  { min: 20, max: 29, label: '20–29' },
  { min: 30, max: 39, label: '30–39' },
  { min: 40, max: Infinity, label: '40+' },
];

/**
 * Colour for a failure count.
 * @param {number|null|undefined} count  null/undefined => not in the BOM at all
 * @param {boolean} linked  whether the part matched any KG entity
 */
export function densityColor(count, linked) {
  if (count === null || count === undefined || !linked) return DENSITY_UNKNOWN;
  if (count <= 0) return DENSITY_ZERO;
  for (let i = 0; i < DENSITY_BINS.length; i += 1) {
    const bin = DENSITY_BINS[i];
    if (count >= bin.min && count <= bin.max) return DENSITY_RAMP[i];
  }
  return DENSITY_RAMP[DENSITY_RAMP.length - 1];
}

/** Legend rows, lowest magnitude first. Order is the reading order. */
export function densityLegend() {
  return [
    { color: DENSITY_UNKNOWN, label: 'No graph data', hint: 'part matched no knowledge-graph entity' },
    { color: DENSITY_ZERO, label: 'None recorded', hint: 'linked, zero recorded failures' },
    ...DENSITY_BINS.map((b, i) => ({
      color: DENSITY_RAMP[i],
      label: b.label,
      hint: `${b.label} recorded failure cases`,
    })),
  ];
}

/**
 * Neutral fill for parts with no fault in DTC mode. Recessive on purpose: the
 * handful of faulted parts must be the only thing that draws the eye. Distinct
 * from DENSITY_UNKNOWN — that one means "we know nothing", this one means
 * "known fine right now".
 */
export const OVERLAY_MUTED = '#333c4d';

/**
 * Chat-focus highlight. This is a *pointer*, not a data encoding — it means
 * "the answer was about this part" — so it uses the app accent rather than a
 * scale colour, and is never on screen at the same time as density or DTC.
 */
export const FOCUS_COLOR = '#66B4C4';

/* ------------------------------------------------------------------- status */

/**
 * Reserved status palette: the Varahi console semantic set (BRAND.md "Product
 * UI"): critical = --bad, warning = --warn, info = --accent. Each clears 4.5:1
 * on #0B1418 and #142329. Do not theme further.
 * `icon` is not decoration: it is the required secondary encoding, so status
 * never depends on hue alone (and survives CVD, print and forced-colors).
 */
export const STATUS = {
  info: { color: '#66B4C4', icon: '●', label: 'Info' },
  warning: { color: '#C9922F', icon: '▲', label: 'Warning' },
  critical: { color: '#DF6259', icon: '■', label: 'Critical' },
};

export const STATUS_ORDER = ['critical', 'warning', 'info'];

export function statusFor(severity) {
  return STATUS[String(severity || '').toLowerCase()] || STATUS.info;
}

/* ------------------------------------------------------- part knowledge */

/**
 * Fault-trace hypothesis rank. Ordinal, single hue (violet), brighter =
 * stronger suspicion — same reading direction as the density ramp. Violet is
 * used by no other overlay, so a rank can never impersonate a status or a
 * density step. Rank is always printed beside the swatch too.
 */
export const TRACE_RANK_RAMP = ['#efc6ff', '#c98cf5', '#9a5fd6', '#7a4cb5'];

/** Parts on a trace path that are not ranked suspects (intermediates). */
export const TRACE_CONTEXT = '#5b6680';

export function traceRankColor(rank) {
  const r = Math.max(1, Math.floor(Number(rank) || 1));
  return TRACE_RANK_RAMP[Math.min(r, TRACE_RANK_RAMP.length) - 1];
}

/**
 * Connection types (CONTRACT §1 connections.yaml). Categorical; every line
 * also carries a text label on hover and in the trace legend.
 */
export const CONNECTION_TYPES = {
  hydraulic: { color: '#4f9dff', label: 'Hydraulic' },
  fuel: { color: '#e3c14b', label: 'Fuel' },
  electrical: { color: '#f2f27a', label: 'Electrical' },
  data: { color: '#3fd6a4', label: 'Data' },
  mechanical: { color: '#b7c0d0', label: 'Mechanical' },
  air: { color: '#9fe3ff', label: 'Air' },
  // Industrial assets (vacuum_pump, VACUUM_SPEC.md §5).
  process_gas: { color: '#a98bff', label: 'Process gas' },
  exhaust: { color: '#c7cdd6', label: 'Exhaust' },
  oil: { color: '#d9913a', label: 'Oil' },
  cooling_air: { color: '#7fd4ff', label: 'Cooling air' },
};

export function connectionType(type) {
  return CONNECTION_TYPES[String(type || '').toLowerCase()]
    || { color: '#b7c0d0', label: String(type || 'Link') };
}

/**
 * Roles a part plays in an /ask answer. Icon + label are mandatory on every
 * rendering (chips, legend) so role never rests on hue alone.
 */
export const ANSWER_ROLES = {
  cause: { color: '#ff7a9a', icon: '◆', label: 'Cause' },
  affected: { color: '#b89cff', icon: '◎', label: 'Affected' },
  check: { color: '#5fd0ff', icon: '✓', label: 'Check' },
  replace: { color: '#ffe07a', icon: '↻', label: 'Replace' },
  context: { color: '#8a9bb8', icon: '·', label: 'Context' },
};

export const ANSWER_ROLE_ORDER = ['cause', 'affected', 'check', 'replace', 'context'];

export function answerRole(role) {
  return ANSWER_ROLES[String(role || '').toLowerCase()] || ANSWER_ROLES.context;
}

/**
 * Knowledge coverage (0..1). Sequential, single hue (teal) so it cannot be
 * read as density (blue) or status. Grey = nothing documented.
 */
export const COVERAGE_NONE = '#2a3140';
export const COVERAGE_RAMP = ['#256e67', '#2a7f76', '#3aa596', '#6fcdbb'];
export const COVERAGE_BINS = [
  { min: 0.000001, max: 0.25, label: '1–25 %' },
  { min: 0.25, max: 0.5, label: '26–50 %' },
  { min: 0.5, max: 0.75, label: '51–75 %' },
  { min: 0.75, max: 1, label: '76–100 %' },
];

export function coverageColor(score) {
  const s = Number(score);
  if (!Number.isFinite(s) || s <= 0) return COVERAGE_NONE;
  for (let i = 0; i < COVERAGE_BINS.length; i += 1) {
    if (s <= COVERAGE_BINS[i].max) return COVERAGE_RAMP[i];
  }
  return COVERAGE_RAMP[COVERAGE_RAMP.length - 1];
}

export function coverageLegend() {
  return [
    { color: COVERAGE_NONE, label: 'Nothing documented' },
    ...COVERAGE_BINS.map((b, i) => ({ color: COVERAGE_RAMP[i], label: b.label })),
  ];
}

/** Failure-mode severity (critical | major | minor) onto the status palette. */
export function failureSeverity(severity) {
  const key = String(severity || '').toLowerCase();
  if (key === 'critical') return { ...STATUS.critical, label: 'Critical' };
  if (key === 'major') return { ...STATUS.warning, label: 'Major' };
  return { ...STATUS.info, label: 'Minor' };
}
